/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import CloseIconWrapper from '@apps/components/CloseIconWrapper';
import { requestGetQuote, requestTac } from '@apps/config/API/quote-config';
import usePostMsgContext from '@apps/containers/WebviewProvider/hooks';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  FormControl,
  FormHelperText,
  Grid,
  InputAdornment,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import NormalIcon from '@stack/assets/icons/normal.svg';
import UrgentIcon from '@stack/assets/icons/urgent.svg';
import createBasePath from '@stack/helpers/lib/createBasePath';
import { useGetResponsive } from '@stack/helpers/lib/responsive';
import isEmpty from 'lodash/isEmpty';
import Image from 'next/image';

interface LocationState {
  state_id: number;
  state_name: string;
}

interface LocationCity {
  city_id: number;
  city_name: string;
  state_id: number;
}

export type QuoteDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  data: {
    carId: number;
    carImage: any;
    carName: string;
    carBrandImage: string;
    lowestPrice: string;
    highestPrice: string;
  };
  location_state: [];
  location_city: [];
  webView?: boolean;
};

export type QuoteDialogFormValues = {
  name: string;
  phoneNo: string;
  tacCode: number;
  city: number;
  state: number;
  purchaseType: string;
};

const QuoteDialog: React.FC<QuoteDialogProps> = (props) => {
  const {
    isOpen,
    onClose,
    data,
    location_city,
    location_state,
    webView = false,
  } = props;

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
    watch,
  } = useForm<QuoteDialogFormValues>({
    mode: 'onBlur',
  });

  // =================== VARIABLES
  const phonePrefixes = [
    '011',
    '012',
    '013',
    '014',
    '016',
    '017',
    '018',
    '019',
  ];

  // =================== HOOKS
  const { clickAction } = usePostMsgContext();
  const { basePath } = createBasePath({ type: 'news' });
  const [isUrgentSelected, setIsUrgentSelected] = useState(true);
  const [isNormalSelected, setIsNormalSelected] = useState(false);
  const [locationState, setLocationState] = useState<LocationState[]>([]);
  const [locationCity, setLocationCity] = useState<LocationCity[]>([]);
  const [filteredCities, setFilteredCities] = useState<LocationCity[]>([]);
  const [prefix, setPrefix] = useState('011');
  const [isCheckedTnC, setIsCheckedTnC] = useState(false);

  const selectedState = watch('state');
  const phoneNumber = watch('phoneNo');

  const [showTacInput, setShowTacInput] = useState(false);
  const [countdown, setCountdown] = useState(0);

  const { lg } = useGetResponsive();
  useEffect(() => {
    setLocationState(location_state);
    setLocationCity(location_city);
    if (selectedState) {
      const filtered = locationCity.filter(
        (city) => city.state_id === selectedState
      );
      setFilteredCities(filtered);
    } else {
      setFilteredCities([]);
    }
  }, [selectedState, locationCity, location_state, location_city]);

  const handleClose = useCallback(() => {
    if (isOpen) {
      reset({
        name: '',
        state: 0,
        phoneNo: '',
        city: 0,
      });
    }
  }, [isOpen]);

  useEffect(() => {
    if (countdown > 0) {
      const interval = setInterval(() => {
        setCountdown((currentCountdown) => currentCountdown - 1);
      }, 1000);

      return () => clearInterval(interval);
    }
  }, [countdown]);

  useEffect(() => {
    handleClose();
  }, [isOpen]);

  const handleSelectPrefix = (event: SelectChangeEvent<string>) => {
    setPrefix(event.target.value);
  };

  const handleRequestTAC = async () => {
    const phone = prefix.slice(1) + phoneNumber;
    await requestTac({
      mobileNumber: `${phone}`,
      tacType: 'NewCarOffer',
    });
    setShowTacInput(true);
    setCountdown(60);
  };

  const onHandleSubmit = async (payload: QuoteDialogFormValues) => {
    const phoneNumber = prefix.slice(1) + payload.phoneNo;

    if (!payload.tacCode) {
      toast.error('Please request and enter the TAC code.');
      return;
    }
    try {
      const response = await requestGetQuote({
        mobile_tac: payload.tacCode,
        new_car_id: data?.carId,
        new_car_offer_customer_city_id: payload.city ?? 0,
        new_car_offer_customer_mobile: phoneNumber,
        new_car_offer_customer_name: payload.name,
        new_car_offer_customer_state_id: payload.state ?? 0,
        new_car_offer_type: isUrgentSelected ? 'urgent' : 'normal',
        tac_type: 'NewCarOffer',
      });
      if (response.msg) {
        toast.success('Get Quote successfully!');
      } else {
        toast.error('Get Quote failed, Please try again.', response.error);
      }
    } catch (error) {
      toast.error('An error occurred.');
    }
  };

  const isRequestTacDisabled = countdown > 0 || isEmpty(phoneNumber);
  const isCounting = countdown > 0;

  return (
    <>
      <Dialog
        onClose={onClose}
        aria-labelledby="customized-dialog-title"
        open={isOpen}
        maxWidth="lg"
        fullScreen={lg}
        PaperProps={{
          sx: {
            borderRadius: '4px',
            border: 'none',
            pt: { xs: webView ? '0' : '51px', lg: 0 },
          },
        }}
      >
        <form
          style={{ display: 'contents' }}
          onSubmit={handleSubmit(onHandleSubmit)}
        >
          <CloseIconWrapper
            title={'Get Quote'}
            onClose={onClose}
            renderFooter={
              <Button
                disabled={!isCheckedTnC}
                fullWidth
                color={'secondary'}
                variant="contained"
                type="submit"
                sx={{
                  fontSize: '1rem',
                  borderRadius: '0',
                  textTransform: 'capitalize',
                  fontWeight: 500,
                }}
              >
                Get Quote Now
              </Button>
            }
            contentProps={{
              dividers: true,
            }}
            titleProps={{
              borderBottom: '0px',
              sx: {
                borderBottom: 'none',
              },
            }}
          >
            <Stack
              display={'flex'}
              flexDirection={'column'}
              justifyContent={'space-between'}
              height={'100%'}
              gap={4}
            >
              <Stack
                display={'flex'}
                flexDirection={{
                  md: 'row',
                  xs: 'column',
                  sm: 'column',
                  lg: 'row',
                }}
                gap={4}
                width={'100%'}
              >
                <Stack
                  flexDirection="column"
                  gap={8}
                  p={4}
                  sx={{
                    backgroundColor: '#F7F7F7',
                  }}
                  justifyContent="center"
                  alignItems={{ xs: 'center', md: 'flex-start' }}
                >
                  <Box
                    display="flex"
                    justifyContent="center"
                    width={{ xs: 170, md: 293 }}
                    height={{ xs: 100, md: 150 }}
                  >
                    <Image
                      src={data?.carImage}
                      alt=""
                      width={293}
                      height={150}
                      style={{
                        height: '100%',
                        width: '100%',
                      }}
                    />
                  </Box>

                  <Stack
                    flexDirection="column"
                    alignItems={{ xs: 'center', md: 'flex-start' }}
                    sx={{
                      width: '100%',
                      justifyContent: 'center',
                    }}
                  >
                    <Stack
                      display={'flex'}
                      flexDirection={'row'}
                      alignItems={'center'}
                      gap={2}
                    >
                      <Image
                        src={data?.carBrandImage}
                        alt=""
                        width={24}
                        height={24}
                      />
                      <Typography
                        fontSize={'1.25rem'}
                        fontWeight={700}
                        color="##1E242F"
                      >
                        {data?.carName}
                      </Typography>
                    </Stack>

                    <Stack flexDirection="row" gap={1} alignItems={'center'}>
                      <Typography fontSize={'0.8rem'}>From RM </Typography>
                      <Typography fontSize={'1.25rem'} fontWeight={700}>
                        {Math.floor(
                          parseFloat(data?.lowestPrice || '0')
                        )?.toLocaleString()}{' '}
                        -{' '}
                        {Math.floor(
                          parseFloat(data?.highestPrice || '0')
                        )?.toLocaleString()}
                      </Typography>
                    </Stack>
                  </Stack>
                </Stack>

                <Stack
                  flexDirection="column"
                  gap={4}
                  pr={2}
                  sx={{
                    width: '100%',
                  }}
                >
                  <Stack flexDirection="column" gap={2} width={'100%'}>
                    <Typography fontSize={'0.875rem'} fontWeight={500}>
                      Name
                    </Typography>
                    <Box display="flex" alignItems="center">
                      <Controller
                        name="name"
                        control={control}
                        render={({ field }) => (
                          <>
                            <TextField
                              variant="outlined"
                              fullWidth
                              placeholder="Please Enter Name"
                              error={!!errors.name}
                              required
                              {...field}
                            />
                            <FormHelperText error>
                              {errors.name?.message}
                            </FormHelperText>
                          </>
                        )}
                      />
                    </Box>
                  </Stack>

                  <Stack flexDirection="column" gap={2} width={'100%'}>
                    <Typography fontSize={'0.875rem'} fontWeight={500}>
                      Mobile No.
                    </Typography>
                    <Box
                      display="flex"
                      alignItems="center"
                      flexDirection="column"
                      gap={2}
                    >
                      <Controller
                        name="phoneNo"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Grid container xs={12}>
                              <Grid item xs={7.5} sm={isCounting ? 10.5 : 9}>
                                <TextField
                                  fullWidth
                                  variant="outlined"
                                  placeholder="3456789"
                                  type="number"
                                  className="disableNumberSpin"
                                  error={!!errors.phoneNo}
                                  required
                                  {...field}
                                  inputProps={{
                                    onWheel: (e) =>
                                      e.target instanceof HTMLElement &&
                                      e.target.blur(),
                                    onKeyDown: (e) => {
                                      if (
                                        e.key === 'ArrowUp' ||
                                        e.key === 'ArrowDown'
                                      ) {
                                        e.preventDefault();
                                      }
                                    },
                                  }}
                                  onKeyDown={(e: {
                                    key: string;
                                    preventDefault: () => any;
                                  }) => {
                                    const invalidChar = [
                                      'e',
                                      'E',
                                      '+',
                                      '-',
                                      Number(e.key) > 0,
                                    ];
                                    invalidChar.includes(e.key) &&
                                      e.preventDefault();
                                  }}
                                  InputProps={{
                                    sx: {
                                      '.MuiSelect-select.MuiSelect-outlined.MuiInputBase-input':
                                        {
                                          paddingRight: '0px !important',
                                          minWidth: '60px !important',
                                        },
                                    },
                                    style: {
                                      paddingLeft: 0,
                                      paddingRight: 0,
                                      borderRadius: 0,
                                    },
                                    startAdornment: (
                                      <InputAdornment
                                        position="start"
                                        sx={{ borderRadius: 0 }}
                                      >
                                        <Select
                                          value={prefix}
                                          onChange={handleSelectPrefix}
                                          sx={{
                                            borderRadius: 0,
                                            borderColor: 0,
                                            '&. MuiSelect-select': {
                                              minWidth: '0rem !important',
                                            },
                                          }}
                                          defaultValue="011"
                                        >
                                          {phonePrefixes.map((prefix) => (
                                            <MenuItem
                                              key={prefix}
                                              value={prefix}
                                            >
                                              {prefix}
                                            </MenuItem>
                                          ))}
                                        </Select>
                                      </InputAdornment>
                                    ),
                                  }}
                                />
                              </Grid>
                              <Grid item xs={4.5} sm={isCounting ? 1.5 : 3}>
                                <Button
                                  fullWidth
                                  disabled={isRequestTacDisabled}
                                  variant="contained"
                                  onClick={handleRequestTAC}
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    border: `1px solid ${
                                      isRequestTacDisabled
                                        ? '#E9E9EC'
                                        : '#1E242F'
                                    }`,
                                    borderRadius: 0,
                                    textTransform: 'none',
                                    backgroundColor: '#1E242F',
                                    '&:hover': {
                                      background: '#1E242F',
                                      boxShadow: 'none',
                                    },
                                    boxShadow: 'none',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {isCounting ? `${countdown}s` : 'Request TAC'}
                                </Button>
                              </Grid>
                            </Grid>
                            <FormHelperText style={{ textAlign: 'left' }} error>
                              {errors.phoneNo?.message}
                            </FormHelperText>
                          </>
                        )}
                      />

                      {showTacInput && (
                        <Controller
                          name="tacCode"
                          control={control}
                          render={({ field }) => (
                            <>
                              <TextField
                                variant="outlined"
                                fullWidth
                                placeholder="Enter TAC code"
                                sx={{
                                  backgroundColor: '#F7F7F7',
                                  '& .MuiOutlinedInput-root': {
                                    '& fieldset': {
                                      borderColor: '#F7F7F7',
                                    },
                                    '&:hover fieldset': {
                                      borderColor: '#F7F7F7',
                                    },
                                    '&.Mui-focused fieldset': {
                                      borderColor: '#F7F7F7',
                                    },
                                  },
                                }}
                                InputProps={{
                                  style: {
                                    borderRadius: 0,
                                    border: '0px !important',
                                    borderColor: '#F7F7F7',
                                  },
                                }}
                                required
                                {...field}
                              />
                              <FormHelperText error>
                                {errors.tacCode?.message}
                              </FormHelperText>
                            </>
                          )}
                        />
                      )}
                    </Box>
                  </Stack>

                  <Stack flexDirection="column" gap={2} width={'100%'}>
                    <Typography fontSize={'0.875rem'} fontWeight={500}>
                      Location
                    </Typography>
                    <Stack
                      flexDirection={{ xs: 'column', md: 'row' }}
                      justifyContent={'space-between'}
                      gap={4}
                    >
                      <Box display="flex" alignItems="center" width={'100%'}>
                        <Controller
                          name="state"
                          control={control}
                          render={({ field: { onChange, value } }) => {
                            return (
                              <>
                                <FormControl required fullWidth>
                                  <Select
                                    sx={{
                                      borderRadius: 0,
                                      backgroundColor: '#F7F7F7',
                                      width: '100%',
                                      '.MuiOutlinedInput-notchedOutline': {
                                        border: 0,
                                      },
                                    }}
                                    onChange={(event) => {
                                      setValue('city', 0);
                                      onChange(event);
                                    }}
                                    value={value || 0}
                                  >
                                    <MenuItem
                                      value={0}
                                      disabled
                                      style={{ display: 'none' }}
                                    >
                                      <Typography
                                        sx={{ opacity: 0.5 }}
                                        color="#A8B5C9"
                                        fontSize={'0.875rem'}
                                      >
                                        State
                                      </Typography>
                                    </MenuItem>
                                    {locationState &&
                                      locationState.map((state) => (
                                        <MenuItem
                                          key={state.state_id}
                                          value={state.state_id}
                                        >
                                          {state.state_name}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                                <FormHelperText error>
                                  {errors.state?.message}
                                </FormHelperText>
                              </>
                            );
                          }}
                        />
                      </Box>
                      <Box display="flex" alignItems="center" width={'100%'}>
                        <Controller
                          name="city"
                          control={control}
                          render={({ field: { onChange, value } }) => {
                            return (
                              <>
                                <FormControl required fullWidth>
                                  <Select
                                    disabled={!selectedState}
                                    sx={{
                                      borderRadius: 0,
                                      width: '100%',
                                      '.MuiOutlinedInput-notchedOutline': {
                                        border: 0,
                                      },
                                    }}
                                    onChange={onChange}
                                    value={value || 0}
                                  >
                                    <MenuItem
                                      value={0}
                                      disabled
                                      style={{ display: 'none' }}
                                    >
                                      <Typography
                                        color="grey.800"
                                        fontSize={'0.875rem'}
                                        sx={{
                                          opacity: 0.5,
                                        }}
                                      >
                                        City
                                      </Typography>
                                    </MenuItem>
                                    {filteredCities &&
                                      filteredCities.map((city) => (
                                        <MenuItem
                                          key={city.city_id}
                                          value={city.city_id}
                                        >
                                          {city.city_name}
                                        </MenuItem>
                                      ))}
                                  </Select>
                                </FormControl>
                                <FormHelperText error>
                                  {errors.city?.message}
                                </FormHelperText>
                              </>
                            );
                          }}
                        />
                      </Box>
                    </Stack>
                  </Stack>

                  <Stack flexDirection={'row'} gap={4} width={'100%'}>
                    <Box
                      key="urgent"
                      border={'1px solid #EDECED'}
                      p={3}
                      gap={2}
                      sx={{
                        backgroundColor: isUrgentSelected
                          ? '#31ACBF26'
                          : '#FFF',
                        borderColor: isUrgentSelected ? '#31ACBF' : '#EDECED',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setIsUrgentSelected(true);
                        setIsNormalSelected(false);
                      }}
                    >
                      <Box minWidth={40}>
                        <UrgentIcon />
                      </Box>

                      <Stack flexDirection="column">
                        <Typography
                          fontSize={'0.8rem'}
                          fontWeight={700}
                          textAlign={{ xs: 'center', md: 'left' }}
                        >
                          Urgent
                        </Typography>
                        <Typography
                          fontSize={'0.75rem'}
                          fontWeight={400}
                          textAlign={{ xs: 'center', md: 'left' }}
                        >
                          Purchase within next 30 days
                        </Typography>
                      </Stack>
                    </Box>

                    <Box
                      key="normal"
                      border={'1px solid #EDECED'}
                      gap={2}
                      p={3}
                      sx={{
                        backgroundColor: isNormalSelected
                          ? '#31ACBF26'
                          : '#FFF',
                        borderColor: isNormalSelected ? '#31ACBF' : '#EDECED',
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: '100%',
                        borderRadius: '4px',
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        setIsNormalSelected(true);
                        setIsUrgentSelected(false);
                      }}
                    >
                      <Box minWidth={40}>
                        <NormalIcon />
                      </Box>
                      <Stack flexDirection="column">
                        <Typography
                          fontSize={'0.8rem'}
                          fontWeight={700}
                          textAlign={{ xs: 'center', md: 'left' }}
                        >
                          Normal
                        </Typography>
                        <Typography
                          fontSize={'0.75rem'}
                          fontWeight={400}
                          textAlign={{ xs: 'center', md: 'left' }}
                        >
                          Purchase within next few months
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                </Stack>
              </Stack>
              <Stack
                flexDirection="row"
                py={4}
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                <Checkbox
                  checked={isCheckedTnC}
                  onChange={(val) => {
                    setIsCheckedTnC(val.target.checked);
                  }}
                  sx={{
                    py: 0,
                    px: 0,
                    mr: 1.5,
                  }}
                />
                <Typography
                  mt={{ xs: 0, md: 0.2 }}
                  fontSize={'0.8rem'}
                  fontWeight={500}
                >
                  By submitting, I acknowledge that I have read and agreed with
                  the{' '}
                  <a
                    href={`${basePath}/terms-of-use`}
                    style={{
                      color: '#31ACBF',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onClick={(event) => {
                      if (webView) {
                        event.preventDefault();
                        clickAction(`${basePath}/webview/terms-of-use`, false, {
                          title: 'Term of Use',
                          params: {
                            headerShown: true,
                            action: 'headerShown',
                          },
                        });
                        clickAction(`${basePath}/webview/terms-of-use`, false, {
                          title: 'Term of Use',
                          params: {
                            newPage: true,
                            headerShown: true,
                          },
                        });
                      }
                    }}
                  >
                    Terms and Conditions
                  </a>{' '}
                  and{' '}
                  <a
                    href={`${basePath}/privacy-policy`}
                    style={{
                      color: '#31ACBF',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                    onClick={(event) => {
                      if (webView) {
                        event.preventDefault();
                        clickAction(
                          `${basePath}/webview/privacy-policy`,
                          false,
                          {
                            title: 'Privacy Policy',
                            params: {
                              headerShown: true,
                              action: 'headerShown',
                            },
                          }
                        );
                        clickAction(
                          `${basePath}/webview/privacy-policy`,
                          false,
                          {
                            title: 'Privacy Policy',
                            params: {
                              newPage: true,
                              headerShown: true,
                            },
                          }
                        );
                      }
                    }}
                  >
                    {' '}
                    Privacy Policy
                  </a>{' '}
                  of CariCarz.
                </Typography>
              </Stack>
            </Stack>
          </CloseIconWrapper>
        </form>
      </Dialog>
    </>
  );
};

export default QuoteDialog;
